import { Injectable } from '@angular/core';
import { SearchResult } from '../models/page-content.model';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class SearchService {
  private availablePages = [
    'introduction',
    'gettingStarted',
    'dashboard',
    'initialScreen',
    'createQuote',
    'eligibility',
    'benefits',
    'additionalDetails',
    'questionnaires',
    'requirements',
    'completeApplication',
    'eSignature',
    'manageESignature',
    'reviewApplication',
    'submit',
    'finalScreen',
    'needHelp',
  ];

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private router: Router
  ) {}

  search(query: string): Observable<SearchResult[]> {
    if (!query || query.trim() === '') {
      return of([]);
    }

    const currentLang = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
    const searchQuery = query.toLowerCase();
    
    console.log('Current language:', currentLang);
    console.log('Available pages to search:', this.availablePages);
    
    // Create an array of observables for each page JSON file
    const pageRequests = this.availablePages.map(pageId => {
      const url = `assets/i18n/${currentLang}/${pageId}.json`;
      console.log('Searching in:', url);
      
      return this.http.get<any>(url).pipe(
        map(pageContent => {
          const results = this.findMatches(pageId, pageContent, searchQuery);
          console.log(`Found ${results.length} matches in ${pageId}`);
          return results;
        }),
        catchError(error => {
          console.error(`Error searching in ${pageId}:`, error);
          return of([]);
        })
      );
    });
    
    // Combine all search results
    return forkJoin(pageRequests).pipe(
      map((results: SearchResult[][]) => {
        // Flatten array of arrays
        const flattenedResults = results.flat();
        return flattenedResults;
      })
    );
  }

  navigateToSearchResult(result: SearchResult): void {
    // Navigate to the search result
    this.router.navigateByUrl(result.path).then(() => {
      // After navigation is complete, check if we need to scroll to a specific section
      if (result.path.includes('#')) {
        const sectionId = result.path.split('#')[1];
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300); // Small delay to ensure the page has loaded
      }
    });
  }

  private stripHtmlTags(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }
  
  private highlightSearchTerms(text: string, searchTerm: string): string {
    if (!searchTerm) return text;
    const sanitizedTerm = searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regex = new RegExp(`(${sanitizedTerm})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  }

  private findMatches(pageId: string, pageContent: any, searchQuery: string): SearchResult[] {
    const results: SearchResult[] = [];
    
    // Helper function to recursively search through sections
    const searchInSections = (sections: any[], pagePath: string) => {
      sections.forEach(section => {
        const sectionContent = this.stripHtmlTags(JSON.stringify(section)).toLowerCase();
        
        if (sectionContent.includes(searchQuery)) {
          // Create excerpt from plain text (no HTML)
          let plainText = this.stripHtmlTags(section.content || '');
          
          // Find context for the search term
          let startIndex = plainText.toLowerCase().indexOf(searchQuery);
          if (startIndex > 75) {
            startIndex = startIndex - 75;
          } else {
            startIndex = 0;
          }
          
          // Create excerpt with context
          let excerpt = plainText.substring(startIndex, startIndex + 200);
          if (startIndex > 0) excerpt = '...' + excerpt;
          if (excerpt.length < plainText.length) excerpt += '...';
          
          // Highlight the search terms
          const highlightedExcerpt = this.highlightSearchTerms(excerpt, searchQuery);
          
          // Determine path based on page ID
          let path = '';
          if (pageId === 'introduction') {
            path = '/introduction';
          } else if (pageId === 'start-application') {
            path = '/completing-application/start-application';
          } else if (pageId === 'account-information') {
            path = '/completing-application/account-information';
          } else if (pageId === 'beneficiary') {
            path = '/completing-application/beneficiary';
          } else if (pageId === 'investment-instructions') {
            path = '/completing-application/investment-instructions';
          } else if (pageId === 'systematic-plan') {
            path = '/completing-application/systematic-plan';
          } else if (pageId === 'know-your-client') {
            path = '/completing-application/know-your-client';
          } else if (pageId === 'special-instructions') {
            path = '/completing-application/special-instructions';
          } else if (pageId === 'review') {
            path = '/completing-application/review';
          } else if (pageId === 'esigning-your-documents') {
            path = '/completing-application/esigning-your-documents';
          } else if (pageId === 'upfront-mandatory-details') {
            path = '/corporate-accounts/upfront-mandatory-details';
          } else if (pageId === 'corporate-beneficiary') {
            path = '/corporate-accounts/corporate-beneficiary';
          } else if (pageId === 'are-there-additional-directors') {
            path = '/corporate-accounts/are-there-additional-directors';
          } else if (pageId === 'kyc-details') {
            path = '/corporate-accounts/kyc-details';
          } else if (pageId === 'additional-attachments') {
            path = '/corporate-accounts/additional-attachments';
          } else if (pageId === 'quebec-corporations') {
            path = '/corporate-accounts/quebec-corporations';
          } else {
            path = `/${pageId}`;
          }
          
          results.push({
            pageId: pageId,
            pageTitleKey: pageContent.title,
            matchingSectionId: section.id,
            matchingSectionTitleKey: section.title,
            excerpt: highlightedExcerpt,
            path: `${path}#${section.id}`
          });
        }
        
        // Search in subsections if they exist
        if (section.subsections && Array.isArray(section.subsections)) {
          searchInSections(section.subsections, pagePath);
        }
      });
    };
    
    // Search the sections
    if (pageContent.sections && pageContent.sections.length > 0) {
      searchInSections(pageContent.sections, '');
    }
    
    return results;
  }
}